import { CustomerI } from './customer';
import { OrderI } from './orders';
import { ProductI } from './products';
import { ServicesI } from './services';
import { SignupFormI } from './singup';
import { PromoCodeI, SlotI, SlotNI, TestingEventI } from './testing-events';

export interface BookingRescI {
  event: TestingEventI;
  booking: BookingI;
}
export interface ReschBookingDataI {
  selectedTimeSlot: Date | null;
  selectedEvent: TestingEventI | null;
  existingBooking: BookingI | null;
  existingOrderId: string | null;
}

export interface CreateRescheduleI {
  existingBookingId: string;
  existingOrderId: string;
  newEventId: string;
  newEventName: string;
  testEventDate: string;
  slotTime: string;
}
export interface BookingDataI {
  selectedTime: Date | null;
  selectedTimeSlot: SlotNI | null;
  selectedSlot: SlotNI | null;
  selectedEvent: TestingEventI | null;
  savedCustomerData: CustomerI | null;
  selectedProduct: ProductI | null;
  selectedService: ServicesI | null;
  userInfo: SignupFormI | null;
  bookingTotalWithTaxServiceFee: number;
  orderTotal: OrderTotalsI | null;
  promotion: PromoCodeI | null;
  workflowStep: string;
}
export interface OrderTotalsI {
  subtotal: number;
  salesTax: number;
  total: number;
  serviceFee: number;
  promocode: string;
  /**
   * Stripe price id the server quote actually resolved (#2015). Echoed back on
   * create-payment-intent as productDefaultPriceId so the charge is pinned to
   * the exact price the customer was shown — deterministic even when the
   * product has multiple active prices. Optional: sessions stored before this
   * field existed fall back to the catalog-derived resolution.
   */
  servicePriceId?: string;
}

/**
 * Event-detail attendee-roster row (#1154). A flat projection of a tenant-scoped
 * `Booking` (from `GET manage-booking-api/event/list/:eventId`, which includes the
 * joined `customer` + `event`) onto exactly the fields the roster table renders:
 * Member / Slot / Status pill / Visit#. Deliberately a dedicated interface rather
 * than passing the raw `BookingI` to the presentational table, so the ui layer
 * never reaches into a relation it didn't ask for.
 */
export interface EventRosterRowI {
  /** Booking id — stable key for the table rows. */
  id: string;
  /** Attendee full name ("First Last"), or the email when the name is absent. */
  memberName: string;
  /** Attendee email address (shown as a secondary line). */
  memberEmail: string;
  /** Slot start time (ISO string) for this booking, or null when unscheduled. */
  slotStartTime: string | null;
  /** Raw booking status enum value (e.g. CONFIRMED) fed to the StatusPill. */
  status: string;
  /** 1-based visit number within this event's roster (booking order). */
  visitNumber: number;
  /**
   * #2648 (Tier 2 item 19) — mirrors `Booking.isPaid`. The admin event-details
   * "Start assessment" action gates on `status === CONFIRMED && isPaid`, the
   * same eligibility pair `createCheckIn` enforces server-side (a CONFIRMED-
   * but-unpaid booking still 400s at check-in) — optional so existing rows
   * built before this field don't need updating.
   */
  isPaid?: boolean;
}

// Frontend-safe mirror of the Prisma `BookingStatusType` enum (string values
// match the API payloads), same convention as `ReservationStatusType` in
// testing-events.ts. Drives the admin booking edit form's status select (#1770).
export enum BookingStatusType {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
  COMPLETED = 'COMPLETED',
  NO_SHOW = 'NO_SHOW',
  RESCHEDULED = 'RESCHEDULED',
  LATE_CHECKIN = 'LATE_CHECKIN',
}

// Frontend-safe mirror of the Prisma `BookingSource` enum (#2738) — who
// initiated the booking. Drives the admin bookings table's Source column.
export enum BookingSource {
  CUSTOMER = 'CUSTOMER',
  ADMIN = 'ADMIN',
  OPERATOR = 'OPERATOR',
}

/**
 * Payload the admin booking edit form emits and the detail page PATCHes to
 * `manage-booking-api/details/:id` (#1770). Deliberately narrow: only the fields
 * the backend `UpdateBookingDto` whitelists as admin-editable — the slot start
 * (ISO string, carries both the booking date and the time slot) and the status.
 */
export interface BookingUpdateI {
  slotStartTime: string;
  status: BookingStatusType;
}

/**
 * Payload the admin Create Booking form emits and posts to
 * `manage-booking-api/admin-create` (#1783 — tenant-scoped, see
 * ManageBookingApiController.createBookingByAdmin / AdminCreateBookingDto).
 */
export interface AdminCreateBookingI {
  customerId: string;
  eventId: string;
  /** Selected service — EventProducts.stripeProductId. */
  productId: string;
  /** ISO instant for the selected slot/time. */
  slotTime: string;
  markAsPaid: boolean;
}

/**
 * #2033 — the store-credit code issued when a booking was cancelled, surfaced
 * persistently on the cancelled booking row (the customer portal previously
 * showed this ONLY in a one-time 12s toast, unrecoverable after reload/dismiss).
 * Only ever populated on a booking whose status is CANCELLED.
 */
export interface BookingCreditI {
  /** The redeemable Stripe promotion code (e.g. "SF7QLCNX"). */
  code: string;
  /** Credit value in DOLLARS, or null if unknown/zero. */
  amount: number | null;
  /** ISO expiration instant, or null if the credit does not expire. */
  expiresAt: string | null;
  /** True once the code has been fully redeemed. */
  redeemed: boolean;
}

export interface BookingI {
  id: string;
  name: string;
  date: string;
  slotTime: string;
  // Scalar slot start time (Prisma Booking.slotStartTime, ISO string). Returned by
  // the tenant roster read (`manage-booking-api/event/list/:eventId`), which does
  // NOT join the `slot` relation — so the roster's Slot column reads this (#1154).
  slotStartTime?: string | null;
  // Scalar slot end time (Prisma Booking.slotEndTime, ISO string). Same shape as
  // slotStartTime above — returned alongside it by any Booking read that does not
  // `select` a narrower projection (#1935: the customer booking-search card reads
  // both to render the appointment's time range).
  slotEndTime?: string | null;
  clubName: string;
  clubAddress: string;
  createdAt: string;
  slotId: string;
  clubId: string;
  eventId: string;
  serviceName: string;
  num: number;
  isAvailable: boolean;
  status: string;
  isSelected: boolean;
  isPaid: boolean;
  isCancelled: boolean;
  /** #1151 — set when the booking transitioned to CONFIRMED; null if never confirmed. Optional: narrower projections may not select it. */
  confirmedAt?: string | null;
  /** #2738 — who initiated the booking (CUSTOMER / ADMIN / OPERATOR). Optional: narrower projections may not select it. */
  source?: BookingSource;
  customer: CustomerI;
  event: TestingEventI;
  slot: SlotI;
  order: OrderI[];
  /** #2033 — populated only for CANCELLED bookings; see {@link BookingCreditI}. */
  credit?: BookingCreditI | null;
  /**
   * #3056 — server-computed eligibility from `GET customer-portal/bookings`
   * (`MyBookingI.canCancel` / `canReschedule`: not terminal AND the appointment
   * is still beyond the event's cutoff window). Advisory only — the cancel /
   * reschedule routes re-validate (#3051). Absent on reads that do not go
   * through that endpoint (e.g. the email-search projection), where the card
   * falls back to client-side date math.
   */
  canCancel?: boolean;
  canReschedule?: boolean;
  /**
   * #3095 — ISO instants from which the API refuses cancel / reschedule (BK020),
   * from the same endpoint. Lets a long-open tab re-check `canCancel` /
   * `canReschedule` against the clock without a refetch. Absent on older API
   * builds and on reads outside that endpoint.
   */
  cancelDeadline?: string | null;
  rescheduleDeadline?: string | null;
}

// Frontend-safe mirror of the backend BookingPaymentMethod enum
// (manage-booking-api/enums/booking-payment-method.enum.ts). Drives the admin
// booking-detail "Mark as paid" action (#2714) — cash / card-on-reader / other,
// no client-supplied amount.
export enum BookingPaymentMethod {
  CASH = 'CASH',
  CARD_ON_READER = 'CARD_ON_READER',
  OTHER = 'OTHER',
}

/**
 * Payload the admin "Mark as paid" action emits and PATCHes to
 * `manage-booking-api/details/:id/mark-paid` (#2714). Deliberately narrow —
 * only the payment method; the server derives the actor from the caller's own
 * verified JWT claim and never accepts a client-supplied amount or actor.
 */
export interface MarkBookingAsPaidDataI {
  paymentMethod: BookingPaymentMethod;
}
